import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { API_DELETE, API_PATCH, DBRequest, useConfig } from '../../../api/api';
import { useTranslation } from 'react-i18next';
import { WorkHourEstimator } from '../../../components/exo/work-hour-estimator/WorkHourEstimator';
import useFetch from '../../../api/useFetch';
import {
  ExoTableToolbar,
  ExoTableToolbarItem,
} from '../../../components/exo/ExoTableToolbar';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ExoDialog from '../../../components/exo/ExoDialog';
import NoDataAnimation from '../../../components/animation/NoDataAnimation';
import SkeletonCard from '../../../components/cards/SkeletonCard';
import Loader from '../../../components/special/Loader';
import { useState } from 'react';
import MoneyLabel from '../../../components/label/MoneyLabel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import Service from '../../../components/exo/work-hour-estimator/Service';
import { updateOrCreate } from '../../../components/special/updateOrCreate';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { ExoVariants } from '../../../components/exo/ExoVariants';
import WorkHourEstimatorSelector from '../../../components/exo/work-hour-estimator/WorkHourEstimatorSelector';
import { tokens } from '../../../global/theme/tokens';
import { renderHours } from '../../time-tracking/worktime';

const TimeCalculationTab = ({ project }) => {
  const { t } = useTranslation();
  const config = useConfig();

  const { apiData, setApiData, isLoading, setIsLoading, reloadApiData } =
    useFetch(`wage-calculations?projectId[eq]=${project.id}`);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  function handleSelectCalc(calc) {
    setEditData(calc);
  }

  const variants = [
    {
      label: t('Load time calculation'),
      Icon: FileDownloadIcon,
      content: <WorkHourEstimatorSelector onSelect={handleSelectCalc} />,
    },
    {
      label: t('New time calculation'),
      Icon: AddCircleOutlineIcon,
      content: (
        <WorkHourEstimator
          startDataCollection={editData}
          project={project}
          onCancel={() => {
            setOpen(false);
            setEditData(null);
          }}
          onSubmit={handleNewCalc}
        />
      ),
    },
  ];

  function handleEdit(calc) {
    setEditData(calc);
  }

  function handleRemove(calc) {
    const empIds = calc.wagePresets.map((emp) => emp.id);

    DBRequest({
      config,
      path: `wage-calculations/${calc.id}`,
      method: API_PATCH({ ...calc, projectId: null, wagePresets: empIds }),
      onResponse: () => handleApiRemove(calc),
    });
  }

  function handleDelete(calc) {
    DBRequest({
      config,
      path: `wage-calculations/${calc.id}`,
      method: API_DELETE(),
      onResponse: () => handleApiRemove(calc),
    });
  }

  function handleApiRemove(calc) {
    const newApiData = apiData.data.filter((el) => el.id != calc.id);
    setApiData({ data: newApiData });
  }

  function handleNewCalc(res) {
    setOpen(false);
    setEditData(null);
    setApiData({ data: updateOrCreate(apiData.data, res.data) });
  }

  return (
    <Box className="w-full flex flex-col relative">
      <ExoTableToolbar>
        <ExoTableToolbarItem
          text={t('Add time calculation')}
          icon={<AddCircleOutlineOutlinedIcon />}
          onClick={() => setOpen(true)}
        />
      </ExoTableToolbar>
      <Loader active={isLoading} />
      {!apiData ? (
        <SkeletonCard height="55" />
      ) : (
        <>
          {apiData.data.length === 0 && (
            <Box className="w-full flex justify-center">
              <NoDataAnimation />
            </Box>
          )}
          {apiData.data.map((data) => (
            <TimeCalculationEntry
              data={data}
              onRemove={handleRemove}
              onDelete={handleDelete}
              onEdit={handleEdit}
            />
          ))}
        </>
      )}
      <ExoDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setEditData(null);
        }}
      >
        <ExoVariants variants={variants} />
      </ExoDialog>

      <ExoDialog
        open={editData}
        onClose={() => {
          setOpen(false);
          setEditData(null);
        }}
      >
        <WorkHourEstimator
          startDataCollection={editData}
          project={project}
          onCancel={() => {
            setOpen(false);
            setEditData(null);
          }}
          onSubmit={handleNewCalc}
        />
      </ExoDialog>
    </Box>
  );
};

const TimeCalculationEntry = ({ data, onRemove, onDelete, onEdit }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [showCHR, setShowCHR] = useState(true);

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box className="flex justify-between items-center w-full pr-4">
          {data.title}
          <Box sx={{ maxWidth: '200px' }}>
            <Typography>
              {t('Total Costs') + ': '}
              <MoneyLabel money={data.totalCost} />
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <ToggleButtonGroup
          value={showCHR}
          size="small"
          onChange={(event, newValue) => {
            if (newValue !== null) setShowCHR(newValue);
          }}
          exclusive
        >
          <ToggleButton value={true}>
            {t('Calculative Hourly Rate')}
          </ToggleButton>
          <ToggleButton value={false}>{t('Services')}</ToggleButton>
        </ToggleButtonGroup>
        {showCHR ? (
          <Box className="flex flex-col gap-2 py-2">
            {data.wagePresets?.map((el, index) => (
              <>
                {index != 0 && <Divider />}
                <Box
                  className="flex flex-col gap-1 px-2 rounded-lg py-1"
                  sx={{
                    backgroundColor: el.color + '30',
                    border: `1px solid ${el.color}80`,
                  }}
                >
                  <Typography fontWeight={600}>{el.label}</Typography>
                  <Box className="flex justify-between">
                    <Typography className="opacity-50">
                      {t('Total Costs')}
                    </Typography>
                    <MoneyLabel money={el.totalCalculativeWage} />
                  </Box>
                  <Box className="flex justify-between">
                    <Typography className="opacity-50">
                      {t('Total Hours')}
                    </Typography>
                    <Typography
                      fontSize="15px"
                      fontWeight={500}
                      className="monospace"
                    >
                      {renderHours(el.totalCalculativeHours * 60) + ' h'}
                    </Typography>
                  </Box>
                </Box>
              </>
            ))}
          </Box>
        ) : (
          data.services?.map((el, index) => (
            <Box className="py-1">
              {index != 0 && <Divider />}
              <Service service={el} />
            </Box>
          ))
        )}
        <Box className="mt-2 flex justify-between">
          <ExoTableToolbarItem
            text={t('Remove time calculation')}
            icon={<RemoveCircleIcon />}
            onClick={() => onRemove(data)}
          />
          <ExoTableToolbarItem
            text={t('Edit time calculation')}
            icon={<EditIcon />}
            onClick={() => onEdit(data)}
          />
          {/*<ExoTableToolbarItem
            text={t('Delete time calculation')}
            icon={<DeleteIcon />}
            onClick={() => onDelete(data)}
          />*/}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default TimeCalculationTab;
